import { useMemo } from 'react';
import {
  parseSong,
  type Segment,
  VIEW_MODES,
  type ViewMode,
  type ViewModeConfig,
} from '@/lib/song';
import { cn } from '@/lib/utils';
import { SectionLabel } from './section-label';

const chordStyle = (config: ViewModeConfig): React.CSSProperties => {
  const { chord } = config;
  return {
    color: chord.color,
    fontWeight: chord.fontWeight,
    fontSize: chord.fontSize,
    fontFamily: chord.fontFamily === 'display' ? 'var(--font-display)' : undefined,
    ...(chord.pill && {
      background: chord.pill.background,
      padding: chord.pill.padding,
      borderRadius: chord.pill.radius,
    }),
  };
};

const SegmentView: React.FC<{
  segment: Segment;
  config: ViewModeConfig;
  showChords: boolean;
}> = ({ segment, config, showChords }) => {
  // acorde solto (`|Gm|`): na linha do texto
  if (segment.inline) {
    if (!showChords) return null;
    return (
      <span className="mx-1 self-end" style={chordStyle(config)}>
        {segment.chord}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col whitespace-pre">
      {showChords && (
        <span
          className="mr-1.5 mb-[3px] min-h-[1.25em] leading-tight"
          style={chordStyle(config)}
        >
          {segment.chord}
        </span>
      )}
      <span style={{ fontWeight: segment.bold ? 700 : undefined }}>{segment.text}</span>
    </span>
  );
};

/**
 * Cifra renderizada: seções, linhas e segmentos (acorde sobre a sílaba) a partir de
 * `parseSong()`. `content` já deve vir transposto (`renderedContent`).
 */
export const SongSheet: React.FC<{
  content: string;
  mode?: ViewMode;
  showChords?: boolean;
  /** Tamanho da letra em px (padrão do modo). */
  fontSize?: number;
  /** Impressão: tudo preto sobre branco, sem cores de seção. */
  plain?: boolean;
  /** Impressão em duas colunas: enche a primeira até o pé da folha, depois a segunda (linha não se parte). */
  columns?: boolean;
  className?: string;
}> = ({
  content,
  mode = 'nitido',
  showChords = true,
  fontSize,
  plain,
  columns,
  className,
}) => {
  const base = VIEW_MODES[mode];
  const config: ViewModeConfig = plain
    ? {
        ...base,
        chord: { color: '#000', fontFamily: 'inter', fontWeight: 800, fontSize: '.85em' },
        lyrics: { ...base.lyrics, color: '#000' },
        sectionLabel: 'pill',
      }
    : base;
  const sections = useMemo(() => parseSong(content), [content]);

  return (
    <div
      className={cn(
        columns ? 'block columns-2 gap-10 [column-fill:auto]' : 'flex flex-col gap-6',
        className,
      )}
      style={{
        ...(plain &&
          ({
            '--section-verso': '#000',
            '--section-pre-refrao': '#000',
            '--section-refrao': '#000',
            '--section-outra': '#000',
          } as React.CSSProperties)),
        color: config.lyrics.color,
        fontSize: fontSize ?? config.lyrics.fontSize,
        lineHeight: 1.35,
      }}
    >
      {sections.map((section, sectionIndex) => {
        const label = section.name && (
          <SectionLabel
            name={section.name}
            kind={section.kind}
            variant={config.sectionLabel}
            className="mb-3"
          />
        );
        const lines = section.lines.map((line, lineIndex) => (
          <div
            key={lineIndex}
            className={cn('flex flex-wrap items-end', columns && 'break-inside-avoid')}
            style={{
              marginTop: line.gapBefore ? `calc(${config.lineGap} * 3)` : config.lineGap,
            }}
          >
            {line.segments.map((segment, segmentIndex) => (
              <SegmentView
                key={segmentIndex}
                segment={segment}
                config={config}
                showChords={showChords}
              />
            ))}
            {line.repeat && (
              <span className="mb-0.5 ml-3 rounded-full border border-accent-line px-2 py-px font-bold text-[11px] text-accent-soft">
                {line.repeat}
              </span>
            )}
          </div>
        ));

        // Em colunas a seção pode se partir entre as colunas, mas linha (acorde + letra) não,
        // e o rótulo vai junto com a primeira linha.
        return columns ? (
          <section key={`${section.name}-${sectionIndex}`} className="mb-6">
            <div className="break-inside-avoid">
              {label}
              {lines[0]}
            </div>
            {lines.slice(1)}
          </section>
        ) : (
          <section key={`${section.name}-${sectionIndex}`} className="flex flex-col">
            {label}
            {lines}
          </section>
        );
      })}
    </div>
  );
};
